/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component — public login box (email lookup then password)
 *   extends:   Component (@glimmer/component)
 *   template:  inline-login.hbs
 *   args:      @applicationController, @taskAfterLogin
 *   uses:      session/log services, beebee-base/lib/utility (beebeeAjaxPost, bbParam), beebee-base/lib/get-config
 *   siblings:  -
 *   tests:     remaining-components-test.js, wave7-coverage-test.js, hook-driven-writes-test.js
 *   gotchas:   loginTask must not run post-login logic after the
 *              session.afterLoginTask.perform() yield — the component can be
 *              destroyed by the re-render it triggers. checkMailTask treats a
 *              4xx from check_mail as "address unknown" (info log), not a fault
 *              (Sentry EMBER-BOOKING-TR regression: unmatched mistyped emails
 *              were being logged as production errors).
 *
 * Two-step public login: checkMailTask looks up the email, loginTask exchanges
 * password + username for an OAuth token and delegates post-login setup to
 * session.afterLoginTask.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { beebeeAjaxPost, bbParam } from 'beebee-base/lib/utility';
import { task } from 'ember-concurrency';
import { inject as iService } from '@ember/service';
import getConfig from 'beebee-base/lib/get-config';

export default class InlineLoginComponent extends Component {
  @iService() session;
  @iService() log;

  @tracked targetGoogle = '_self';
  @tracked accesso = null;
  @tracked email = null;
  @tracked password = null;

  // --- lifecycle ---

  @action
  setup(element) {
    if (window.location !== window.parent.location) {
      this.targetGoogle = '_blank';
    } else {
      this.targetGoogle = '_self';
    }
  }

  // --- actions ---

  @action
  resetAccesso() {
    this.accesso = null;
  }

  @action
  submitLogin(e) {
    e.preventDefault();
    this.loginTask.perform();
  }

  @action
  submitCheckMail(e) {
    e.preventDefault();
    this.checkMailTask.perform();
  }

  // --- tasks ---

  @task
  *loginTask() {
    var data = {
      grant_type: 'password',
      username: this.accesso?.dati?.username ? this.accesso.dati.username.trim() : null,
      password: this.password ? this.password.trim() : null,
    };

    try {
      const response = yield fetch(`${getConfig(this).APP.urlApi}/../oauth/token`, {
        method: 'POST',
        headers: {
          Authorization: 'Basic YWJjMTIzOnNzaC1zZWNyZXQ=',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: bbParam(data),
      });

      if (!response.ok) {
        throw new Error('login failed');
      }

      const accesso = yield response.json();

      if (accesso.access_token) {
        // Delegates everything to the session service: authenticate, load /me, configure UI,
        // reset showLogin, run taskAfterLogin and force router.refresh().
        // Do NOT put post-login logic after this yield: the component may be destroyed
        // by the re-render (e.g. requireLoginForBooking becomes false).
        this.session.afterLoginTask.perform(accesso.access_token, {
          controller: this.args.applicationController,
          taskAfterLogin: this.args.taskAfterLogin,
        });
      } else {
        this.accesso = { ...this.accesso, error: true };
      }
    } catch (e) {
      this.accesso = { ...this.accesso, error: true };
    }
  }

  @task
  *checkMailTask() {
    try {
      const accesso = yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/check_mail`, null, {
        mail: this.email,
      });

      this.accesso = {
        effettuato: true,
        dati: accesso,
      };
    } catch (e) {
      // A 4xx from `check_mail` is the server ANSWERING, not failing: the
      // address is unknown or not acceptable. Treating it as a fault told the
      // customer "comunication error" — which reads as "the site is broken,
      // come back later" for what is really "we don't know that address" — and
      // logged at `warn`, which in production is `Sentry.captureMessage`, so
      // every mistyped e-mail on the public login gate opened a production
      // issue (EMBER-BOOKING-TR, "422 unknown"). Show the account-not-found
      // message the lookup form already carries, and log it as information.
      // Anything else (network, 5xx) stays a real failure on both counts — and so
      // does a retryable 4xx: a 429/408 is "come back later", not "unknown
      // address", so only the two statuses that MEAN unknown are accepted here
      // (narrowed with the e-commerce twin, POP-401).
      const status = e?.status ?? 0;
      if (status === 404 || status === 422) {
        this.log.info('[inline-login] check_mail refused the address:', status);
        this.accesso = { effettuato: true };
        return;
      }

      this.log.warn('[inline-login] checkMailTask failed:', e);
      this.accesso = { error: true };
    }
  }
}
